import { ConfigGroup, ConfigItemType } from '@ecosystem/newron-client-lib'
import { InsightData } from 'containers/charts'
import { allCategoryGroupNames } from 'config/filters'
import { Answer, CategoryGroupName, Filter, Schema } from 'typings/filters'
import {
  ConfigItemsInfo,
  InsightDataInfo,
  ViewStateInfo,
} from '@ecosystem/newron-client-collections/build/shared/types'
import { fetchDataFromApollo, fetchFilterValues } from '..'
import { AlertMessages } from 'util/errorMessages'
import { Cell } from 'reducers/subscription/user'
import {
  checkForErrors,
  constructAudienceSegmentThumbnail,
  getAllSelectedMultiSelectValues,
  GetConfigItemsArgs,
  getFilterNames,
  getFilters,
  getLabelAndCategoryGroupByKey,
  getTimeFrameFilters,
  getUpdatedFilters,
} from './utility'
import { ProductGroupMarketFilterGuardMsg } from 'components/FilterSummary'

const InsightEditCallback = () => {
  const getConfigItems = async (args: GetConfigItemsArgs): Promise<ConfigItemsInfo> => {
    const context = args?.data?.graphqlVars?.context
    const schema = args?.data?.graphqlVars?.schema ?? Schema.CIE
    const allFilters = await fetchDataFromApollo({ schema, context })
    const filters = getFilters(args.data.graphqlVars?.filterNames ?? [], allFilters, context)
    return {
      configItems: filters,
      errorMessage: filters?.length === 0 ? AlertMessages.ERROR_MESSAGE : '',
    }
  }

  const clearConfigValues = async ({ data }): Promise<ConfigItemsInfo> => {
    const context = data?.graphqlVars?.context
    const schema = data?.graphqlVars?.schema ?? Schema.CIE
    const allFilters = await fetchDataFromApollo({ schema, context })
    const filters = getFilters([], allFilters, context)
    return {
      configItems: filters,
      errorMessage: `${ProductGroupMarketFilterGuardMsg.errorMsg} (${ProductGroupMarketFilterGuardMsg.errorDetail})`,
    }
  }

  const applyConfigValue = ({ configItems, newValue }): ConfigItemsInfo => {
    if (newValue?.key) {
      const updatedConfigItems: (ConfigGroup & { context: Cell[] })[] = getUpdatedFilters(
        configItems,
        newValue
      )

      return {
        configItems: updatedConfigItems,
        errorMessage: checkForErrors(updatedConfigItems),
      }
    }
    return { configItems: configItems }
  }

  const getInsightData = ({ data, configItems }): InsightDataInfo<InsightData> => {
    const filterNames = getFilterNames(configItems) ?? []
    const timeFrameFilters = getTimeFrameFilters(data)
    const allFilterNames = [...filterNames, ...timeFrameFilters]

    const updatedData = {
      ...data,
      graphqlVars: {
        ...data.graphqlVars,
        filterNames: allFilterNames,
        filters: allFilterNames?.map(({ variable_id, answer_code }) => ({
          variable_id,
          answer_code,
        })),
      },
    }

    return { data: updatedData }
  }

  const onRequestOptions = async ({ data, configItems, key }): Promise<ConfigItemsInfo> => {
    const context = data?.graphqlVars?.context
    const schema = data?.graphqlVars?.schema ?? Schema.CIE
    const timeFrameFilters = getTimeFrameFilters(data)?.map(
      ({ variable_id, answer_code }: { variable_id: number; answer_code: number }) => ({
        variable_id,
        answer_code,
      })
    )
    const filters = getAllSelectedMultiSelectValues(configItems)?.map(
      ({ variable_id, answer_code }) => ({
        variable_id,
        answer_code,
      })
    )
    const filterValues = await fetchFilterValues({
      schema,
      context,
      variableId: +key,
      filters: [...filters, ...timeFrameFilters],
    })

    if (!filterValues) {
      return {
        configItems,
        errorMessage: AlertMessages.ERROR_MESSAGE,
      }
    }
    const { label: subCategoryLabel, categoryGroup } = getLabelAndCategoryGroupByKey(
      configItems,
      key
    )

    const newOptions = (filterValues ?? []).map((ans: Answer) => ({
      value: ans?.answer_code?.toString(),
      categoryGroup,
      desc: `${subCategoryLabel}: ${ans?.label}`,
      variable_id: +key,
      label: ans.label ?? '',
      answer_code: ans.answer_code,
      answer_id: ans.answer_id,
      category_name: ans.category_name ?? '',
      leadingIcon: constructAudienceSegmentThumbnail(+key, ans.answer_id, ans.label),
    }))

    const updateItems = (items: any[]): any[] =>
      items.map((item) => {
        if (item?.type === ConfigItemType.Group && Array.isArray(item.value)) {
          return { ...item, value: updateItems(item.value) }
        }

        if (item.type && item?.key === key) {
          return { ...item, options: newOptions, isLoading: false }
        }

        return item
      })

    const updatedConfigItems = Array.isArray(configItems) ? updateItems(configItems) : configItems

    return {
      configItems: updatedConfigItems,
    }
  }

  const getViewStateInfo = (args): ViewStateInfo<InsightData> => {
    const { data, pageView } = args
    const appliedFilters: any = data?.graphqlVars?.filterNames?.reduce(
      (acc: Record<CategoryGroupName, Filter[]>, curr: Filter) => {
        const categoryGroupName: CategoryGroupName = curr?.categoryGroup
        acc[categoryGroupName].push({
          answer_code: curr.answer_code,
          answer_id: curr.answer_id,
          desc: curr?.desc,
          label: curr?.label,
          variable_id: curr?.variable_id,
          categoryGroup: curr?.categoryGroup,
        })
        return acc
      },
      allCategoryGroupNames.reduce(
        (acc, groupName) => ({ ...acc, [groupName]: [] }),
        {} as Record<CategoryGroupName, Filter[]>
      )
    )

    appliedFilters.ProductReview_Base = appliedFilters.General_Base?.map((f: Filter) => ({
      ...f,
      categoryGroup: CategoryGroupName.ProductReview_Base,
    }))

    return { viewState: { ...pageView?.data, appliedFilters } }
  }

  return {
    getConfigItems,
    clearConfigValues,
    applyConfigValue,
    getInsightData,
    getViewStateInfo,
    onRequestOptions,
  }
}

export default InsightEditCallback
